/**
 * Unified detail drawer: MUI Drawer with consistent header and responsive width.
 * Use for Rent, Station, Powerbank, User, Transaction, Tariff detail panels.
 *
 * Mobile (esp. iOS): paper must never exceed the viewport; content scrolls
 * vertically with overflow-x hidden so Safari does not zoom/pan the page.
 */
import React from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';

const PAPER_SX = {
  width: { xs: '100%', sm: 420 },
  maxWidth: '100vw',
  boxSizing: 'border-box',
  flexShrink: 0,
  overflowX: 'hidden',
  // iOS dynamic viewport (address bar / safe areas)
  '@supports (width: 100dvw)': {
    maxWidth: '100dvw',
  },
  pt: 'env(safe-area-inset-top)',
  pb: 'env(safe-area-inset-bottom)',
};

/** Unified transition: smooth slide like in Finance/Tariffs for all detail drawers. */
const TRANSITION_DURATION = { enter: 300, exit: 250 };

export default function DetailDrawer({ title, open, onClose, children }) {
  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={TRANSITION_DURATION}
      ModalProps={{ keepMounted: false }}
      sx={{
        width: { xs: '100%', sm: 420 },
        flexShrink: 0,
        '& .MuiDrawer-paper': PAPER_SX,
      }}
    >
      <Box
        sx={{
          height: '100%',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <Typography variant="h6" component="h2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ ml: 0.5 }} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            p: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>{children}</Box>
        </Box>
      </Box>
    </Drawer>
  );
}

DetailDrawer.propTypes = {
  title: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};
